import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Save, ArrowLeft, Briefcase, FileText, Shield, Lock, Heart, Users, Scale, AlertCircle } from 'lucide-react';
import { API } from '../App';

export default function PersonalDetailsForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [jobCategories, setJobCategories] = useState([]);
  const [visaTypes, setVisaTypes] = useState([]);
  
  const [formData, setFormData] = useState({
    selected_categories: [],
    basic_info: {
      full_name: '', preferred_name: '', email: '', phone: '', address: '', postcode: '',
      eligibility_to_work_uk: null, visa_type: '', visa_expiry: '', share_code: '',
      has_driving_license: null, driving_license_number: ''
    },
    employment_eligibility: {
      legally_allowed_work_uk: null, has_uk_passport: null, has_brp: null,
      brp_share_code: '', settlement_status: '', visa_category: '',
      visa_expiry_date: '', work_hour_limitations: ''
    },
    criminal_record: {
      willing_background_check: null, has_unspent_convictions: null, conviction_details: ''
    },
    security_military: {
      served_armed_forces: null, eligible_security_clearance: null, clearance_level: '',
      lived_outside_uk_12months: null, countries_lived_in: [], consent_international_checks: null
    },
    disability: {
      has_disability: null, requires_adjustments: null, adjustment_details: ''
    },
    diversity: {
      gender: '', ethnicity: '', religion: '', sexual_orientation: '', age_range: ''
    },
    gdpr_consent: {
      consent_process_data: false, consent_contact: false, consent_store_data: false
    }
  });

  const [visibleSections, setVisibleSections] = useState({
    basic_info: true, employment_eligibility: false, criminal_record: false,
    security_military: false, disability: false, diversity: false, gdpr_consent: true
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [categoriesRes, visaRes, detailsRes] = await Promise.all([
        axios.get(`${API}/profile/job-categories`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }),
        axios.get(`${API}/profile/visa-types`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }),
        axios.get(`${API}/profile/personal-details`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true })
      ]);
      
      setJobCategories(categoriesRes.data.categories || []);
      setVisaTypes(visaRes.data.visa_types || []);
      
      if (detailsRes.data && detailsRes.data.user_id) {
        setFormData(prevData => ({ ...prevData, ...detailsRes.data }));
        if (detailsRes.data.selected_categories) {
          updateVisibleSections(detailsRes.data.selected_categories, categoriesRes.data.categories);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const updateVisibleSections = (selectedCats, allCategories) => {
    // Only show: Job Categories, Basic Info, and GDPR Consent
    setVisibleSections({
      basic_info: true, employment_eligibility: false,
      criminal_record: false, security_military: false,
      disability: false, diversity: false, gdpr_consent: true
    });
  };

  const handleCategoryChange = (categoryValue) => {
    const newCategories = formData.selected_categories.includes(categoryValue)
      ? formData.selected_categories.filter(c => c !== categoryValue)
      : [...formData.selected_categories, categoryValue];
    setFormData(prev => ({ ...prev, selected_categories: newCategories }));
    updateVisibleSections(newCategories, jobCategories);
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  };

  const handleArrayInputChange = (section, field, value) => {
    const array = value.split(',').map(item => item.trim()).filter(item => item);
    handleInputChange(section, field, array);
  };

  const handleSave = async () => {
    try {
      if (!formData.gdpr_consent.consent_process_data || !formData.gdpr_consent.consent_contact || !formData.gdpr_consent.consent_store_data) {
        toast.error('Please accept all GDPR consent requirements');
        return;
      }
      setSaving(true);
      const token = localStorage.getItem('token');
      const dataToSave = { ...formData, gdpr_consent: { ...formData.gdpr_consent, consent_date: new Date().toISOString() } };
      await axios.post(`${API}/profile/personal-details`, dataToSave, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      toast.success('Personal details saved successfully!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Personal Details & Government ID</h1>
          <p className="text-gray-600 mt-2">Complete your profile with required information</p>
        </div>

        {/* Category Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Briefcase className="w-5 h-5 mr-2 text-indigo-600" />
              1. Job Categories
            </CardTitle>
            <CardDescription>Select all that apply. This determines which sections you need to complete.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {jobCategories.map((cat) => (
                <label key={cat.value} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <Checkbox checked={formData.selected_categories.includes(cat.value)} onCheckedChange={() => handleCategoryChange(cat.value)} />
                  <span className="font-medium">{cat.label}</span>
                </label>
              ))}
            </div>
            {formData.selected_categories.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800"><strong>Selected:</strong> {formData.selected_categories.length} categor{formData.selected_categories.length === 1 ? 'y' : 'ies'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 1: Basic Personal Information */}
        {visibleSections.basic_info && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="w-5 h-5 mr-2 text-indigo-600" />
                2. Basic Personal Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input id="full_name" value={formData.basic_info.full_name} onChange={(e) => handleInputChange('basic_info', 'full_name', e.target.value)} placeholder="As on official documents" />
                </div>
                <div>
                  <Label htmlFor="preferred_name">Preferred Name</Label>
                  <Input id="preferred_name" value={formData.basic_info.preferred_name} onChange={(e) => handleInputChange('basic_info', 'preferred_name', e.target.value)} placeholder="Optional" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={formData.basic_info.email} onChange={(e) => handleInputChange('basic_info', 'email', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" type="tel" value={formData.basic_info.phone} onChange={(e) => handleInputChange('basic_info', 'phone', e.target.value)} />
                </div>
              </div>

              <div>
                <Label htmlFor="address">Address *</Label>
                <Input id="address" value={formData.basic_info.address} onChange={(e) => handleInputChange('basic_info', 'address', e.target.value)} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="postcode">Postcode *</Label>
                  <Input id="postcode" value={formData.basic_info.postcode} onChange={(e) => handleInputChange('basic_info', 'postcode', e.target.value)} />
                </div>
                <div>
                  <Label>Eligibility to work in the UK *</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center space-x-2">
                      <input type="radio" name="eligibility" checked={formData.basic_info.eligibility_to_work_uk === true} onChange={() => handleInputChange('basic_info', 'eligibility_to_work_uk', true)} />
                      <span>Yes</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="radio" name="eligibility" checked={formData.basic_info.eligibility_to_work_uk === false} onChange={() => handleInputChange('basic_info', 'eligibility_to_work_uk', false)} />
                      <span>No</span>
                    </label>
                  </div>
                </div>
              </div>

              {formData.basic_info.eligibility_to_work_uk === false && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div>
                    <Label htmlFor="visa_type">Visa Type</Label>
                    <Input id="visa_type" value={formData.basic_info.visa_type} onChange={(e) => handleInputChange('basic_info', 'visa_type', e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="visa_expiry">Visa Expiry Date</Label>
                    <Input id="visa_expiry" type="date" value={formData.basic_info.visa_expiry} onChange={(e) => handleInputChange('basic_info', 'visa_expiry', e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="share_code">Share Code</Label>
                    <Input id="share_code" value={formData.basic_info.share_code} onChange={(e) => handleInputChange('basic_info', 'share_code', e.target.value)} />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Do you have a Driving License? *</Label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center space-x-2">
                      <input type="radio" name="has_driving_license" checked={formData.basic_info.has_driving_license === true} onChange={() => handleInputChange('basic_info', 'has_driving_license', true)} />
                      <span>Yes</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="radio" name="has_driving_license" checked={formData.basic_info.has_driving_license === false} onChange={() => handleInputChange('basic_info', 'has_driving_license', false)} />
                      <span>No</span>
                    </label>
                  </div>
                </div>
                {formData.basic_info.has_driving_license === true && (
                  <div>
                    <Label htmlFor="driving_license_number">Driving License Number</Label>
                    <Input id="driving_license_number" value={formData.basic_info.driving_license_number} onChange={(e) => handleInputChange('basic_info', 'driving_license_number', e.target.value)} />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 2: Employment Eligibility */}
        {visibleSections.employment_eligibility && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="w-5 h-5 mr-2 text-indigo-600" />
                2. Employment Eligibility (Right to Work)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Are you legally allowed to work in the UK? *</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="legally_allowed" checked={formData.employment_eligibility.legally_allowed_work_uk === true} onChange={() => handleInputChange('employment_eligibility', 'legally_allowed_work_uk', true)} />
                    <span>Yes</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="legally_allowed" checked={formData.employment_eligibility.legally_allowed_work_uk === false} onChange={() => handleInputChange('employment_eligibility', 'legally_allowed_work_uk', false)} />
                    <span>No</span>
                  </label>
                </div>
              </div>

              {formData.employment_eligibility.legally_allowed_work_uk === true && (
                <div className="space-y-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div>
                    <Label>Do you have a UK Passport?</Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center space-x-2">
                        <input type="radio" name="has_uk_passport" checked={formData.employment_eligibility.has_uk_passport === true} onChange={() => handleInputChange('employment_eligibility', 'has_uk_passport', true)} />
                        <span>Yes</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input type="radio" name="has_uk_passport" checked={formData.employment_eligibility.has_uk_passport === false} onChange={() => handleInputChange('employment_eligibility', 'has_uk_passport', false)} />
                        <span>No</span>
                      </label>
                    </div>
                  </div>

                  {formData.employment_eligibility.has_uk_passport === true && (
                    <div>
                      <Label>Upload UK Passport (optional)</Label>
                      <Input type="file" accept="image/*,.pdf" onChange={() => toast.info('File upload feature coming soon')} />
                    </div>
                  )}

                  <div>
                    <Label>Do you have a BRP?</Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center space-x-2">
                        <input type="radio" name="has_brp" checked={formData.employment_eligibility.has_brp === true} onChange={() => handleInputChange('employment_eligibility', 'has_brp', true)} />
                        <span>Yes</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input type="radio" name="has_brp" checked={formData.employment_eligibility.has_brp === false} onChange={() => handleInputChange('employment_eligibility', 'has_brp', false)} />
                        <span>No</span>
                      </label>
                    </div>
                  </div>

                  {formData.employment_eligibility.has_brp === true && (
                    <div className="space-y-4">
                      <div>
                        <Label>BRP Share Code</Label>
                        <Input value={formData.employment_eligibility.brp_share_code} onChange={(e) => handleInputChange('employment_eligibility', 'brp_share_code', e.target.value)} placeholder="Enter share code" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Upload BRP Front (optional)</Label>
                          <Input type="file" accept="image/*,.pdf" />
                        </div>
                        <div>
                          <Label>Upload BRP Back (optional)</Label>
                          <Input type="file" accept="image/*,.pdf" />
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="settlement_status">Pre-settled / Settled Status</Label>
                    <select id="settlement_status" value={formData.employment_eligibility.settlement_status} onChange={(e) => handleInputChange('employment_eligibility', 'settlement_status', e.target.value)} className="w-full px-3 py-2 border rounded-md">
                      <option value="">Select status</option>
                      <option value="none">None</option>
                      <option value="pre-settled">Pre-settled</option>
                      <option value="settled">Settled</option>
                    </select>
                  </div>
                </div>
              )}

              {formData.employment_eligibility.legally_allowed_work_uk === false && (
                <div className="space-y-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div>
                    <Label htmlFor="visa_category">Visa Category *</Label>
                    <select id="visa_category" value={formData.employment_eligibility.visa_category} onChange={(e) => handleInputChange('employment_eligibility', 'visa_category', e.target.value)} className="w-full px-3 py-2 border rounded-md">
                      <option value="">Select visa type</option>
                      {visaTypes.map((visa) => (
                        <option key={visa.value} value={visa.value}>{visa.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="visa_expiry_date">Visa Expiry Date *</Label>
                      <Input id="visa_expiry_date" type="date" value={formData.employment_eligibility.visa_expiry_date} onChange={(e) => handleInputChange('employment_eligibility', 'visa_expiry_date', e.target.value)} />
                    </div>
                    <div>
                      <Label htmlFor="work_hour_limitations">Work Hour Limitations</Label>
                      <Input id="work_hour_limitations" value={formData.employment_eligibility.work_hour_limitations} onChange={(e) => handleInputChange('employment_eligibility', 'work_hour_limitations', e.target.value)} placeholder="e.g., 20 hours per week" />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Section 3: Criminal Record & DBS */}
        {visibleSections.criminal_record && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Lock className="w-5 h-5 mr-2 text-indigo-600" />
                3. Criminal Record & Background (DBS)
              </CardTitle>
              <CardDescription>Required for selected job categories</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Are you willing to undergo a background check? *</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="willing_background_check" checked={formData.criminal_record.willing_background_check === true} onChange={() => handleInputChange('criminal_record', 'willing_background_check', true)} />
                    <span>Yes</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="willing_background_check" checked={formData.criminal_record.willing_background_check === false} onChange={() => handleInputChange('criminal_record', 'willing_background_check', false)} />
                    <span>No</span>
                  </label>
                </div>
              </div>

              <div>
                <Label>Do you have any unspent convictions? *</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="has_unspent_convictions" checked={formData.criminal_record.has_unspent_convictions === true} onChange={() => handleInputChange('criminal_record', 'has_unspent_convictions', true)} />
                    <span>Yes</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="has_unspent_convictions" checked={formData.criminal_record.has_unspent_convictions === false} onChange={() => handleInputChange('criminal_record', 'has_unspent_convictions', false)} />
                    <span>No</span>
                  </label>
                </div>
              </div>

              {formData.criminal_record.has_unspent_convictions === true && (
                <div>
                  <Label htmlFor="conviction_details">Please provide details</Label>
                  <textarea id="conviction_details" value={formData.criminal_record.conviction_details} onChange={(e) => handleInputChange('criminal_record', 'conviction_details', e.target.value)} className="w-full px-3 py-2 border rounded-md min-h-[100px]" placeholder="Provide details" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Section 4: Security & Military */}
        {visibleSections.security_military && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="w-5 h-5 mr-2 text-indigo-600" />
                4. Security & Military
              </CardTitle>
              <CardDescription>Required for Aviation, Defense & Security roles</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Have you ever served in the armed forces?</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="served_armed_forces" checked={formData.security_military.served_armed_forces === true} onChange={() => handleInputChange('security_military', 'served_armed_forces', true)} />
                    <span>Yes</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="served_armed_forces" checked={formData.security_military.served_armed_forces === false} onChange={() => handleInputChange('security_military', 'served_armed_forces', false)} />
                    <span>No</span>
                  </label>
                </div>
              </div>

              <div>
                <Label>Are you eligible for UK Security Clearance?</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="eligible_security_clearance" checked={formData.security_military.eligible_security_clearance === true} onChange={() => handleInputChange('security_military', 'eligible_security_clearance', true)} />
                    <span>Yes</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="eligible_security_clearance" checked={formData.security_military.eligible_security_clearance === false} onChange={() => handleInputChange('security_military', 'eligible_security_clearance', false)} />
                    <span>No</span>
                  </label>
                </div>
              </div>

              {formData.security_military.eligible_security_clearance === true && (
                <div>
                  <Label htmlFor="clearance_level">Clearance Level</Label>
                  <select id="clearance_level" value={formData.security_military.clearance_level} onChange={(e) => handleInputChange('security_military', 'clearance_level', e.target.value)} className="w-full px-3 py-2 border rounded-md">
                    <option value="">Select level</option>
                    <option value="none">None</option>
                    <option value="SC">SC (Security Check)</option>
                    <option value="DV">DV (Developed Vetting)</option>
                  </select>
                </div>
              )}

              <div>
                <Label>Have you lived outside the UK for more than 12 months in the last 5 years?</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="lived_outside_uk" checked={formData.security_military.lived_outside_uk_12months === true} onChange={() => handleInputChange('security_military', 'lived_outside_uk_12months', true)} />
                    <span>Yes</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="lived_outside_uk" checked={formData.security_military.lived_outside_uk_12months === false} onChange={() => handleInputChange('security_military', 'lived_outside_uk_12months', false)} />
                    <span>No</span>
                  </label>
                </div>
              </div>

              {formData.security_military.lived_outside_uk_12months === true && (
                <>
                  <div>
                    <Label htmlFor="countries_lived_in">Countries Lived In (comma-separated)</Label>
                    <Input id="countries_lived_in" value={formData.security_military.countries_lived_in?.join(', ') || ''} onChange={(e) => handleArrayInputChange('security_military', 'countries_lived_in', e.target.value)} placeholder="e.g., France, Germany" />
                  </div>

                  <div>
                    <Label>Do you consent to international background checks?</Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center space-x-2">
                        <input type="radio" name="consent_international_checks" checked={formData.security_military.consent_international_checks === true} onChange={() => handleInputChange('security_military', 'consent_international_checks', true)} />
                        <span>Yes</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input type="radio" name="consent_international_checks" checked={formData.security_military.consent_international_checks === false} onChange={() => handleInputChange('security_military', 'consent_international_checks', false)} />
                        <span>No</span>
                      </label>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Section 5: Disability */}
        {visibleSections.disability && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Heart className="w-5 h-5 mr-2 text-indigo-600" />
                5. Disability (Voluntary)
              </CardTitle>
              <CardDescription>This section is optional and helps us provide necessary accommodations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Do you consider yourself to have a disability?</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="has_disability" checked={formData.disability.has_disability === true} onChange={() => handleInputChange('disability', 'has_disability', true)} />
                    <span>Yes</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="has_disability" checked={formData.disability.has_disability === false} onChange={() => handleInputChange('disability', 'has_disability', false)} />
                    <span>No</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="has_disability" checked={formData.disability.has_disability === null} onChange={() => handleInputChange('disability', 'has_disability', null)} />
                    <span>Prefer not to say</span>
                  </label>
                </div>
              </div>

              <div>
                <Label>Do you require any reasonable adjustments during interview?</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="requires_adjustments" checked={formData.disability.requires_adjustments === true} onChange={() => handleInputChange('disability', 'requires_adjustments', true)} />
                    <span>Yes</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="requires_adjustments" checked={formData.disability.requires_adjustments === false} onChange={() => handleInputChange('disability', 'requires_adjustments', false)} />
                    <span>No</span>
                  </label>
                </div>
              </div>

              {formData.disability.requires_adjustments === true && (
                <div>
                  <Label htmlFor="adjustment_details">Please provide details</Label>
                  <textarea id="adjustment_details" value={formData.disability.adjustment_details} onChange={(e) => handleInputChange('disability', 'adjustment_details', e.target.value)} className="w-full px-3 py-2 border rounded-md min-h-[100px]" placeholder="Describe the adjustments you need" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Section 6: Diversity Monitoring */}
        {visibleSections.diversity && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="w-5 h-5 mr-2 text-indigo-600" />
                6. Diversity Monitoring (Voluntary)
              </CardTitle>
              <CardDescription>This information helps us ensure equal opportunities. All fields are optional.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gender">Gender</Label>
                  <select id="gender" value={formData.diversity.gender} onChange={(e) => handleInputChange('diversity', 'gender', e.target.value)} className="w-full px-3 py-2 border rounded-md">
                    <option value="">Prefer not to say</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="ethnicity">Ethnicity</Label>
                  <select id="ethnicity" value={formData.diversity.ethnicity} onChange={(e) => handleInputChange('diversity', 'ethnicity', e.target.value)} className="w-full px-3 py-2 border rounded-md">
                    <option value="">Prefer not to say</option>
                    <option value="white">White</option>
                    <option value="asian">Asian / Asian British</option>
                    <option value="black">Black / African / Caribbean / Black British</option>
                    <option value="mixed">Mixed / Multiple ethnic groups</option>
                    <option value="other">Other ethnic group</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="religion">Religion</Label>
                  <select id="religion" value={formData.diversity.religion} onChange={(e) => handleInputChange('diversity', 'religion', e.target.value)} className="w-full px-3 py-2 border rounded-md">
                    <option value="">Prefer not to say</option>
                    <option value="none">No religion</option>
                    <option value="christian">Christian</option>
                    <option value="muslim">Muslim</option>
                    <option value="hindu">Hindu</option>
                    <option value="sikh">Sikh</option>
                    <option value="jewish">Jewish</option>
                    <option value="buddhist">Buddhist</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="sexual_orientation">Sexual Orientation</Label>
                  <select id="sexual_orientation" value={formData.diversity.sexual_orientation} onChange={(e) => handleInputChange('diversity', 'sexual_orientation', e.target.value)} className="w-full px-3 py-2 border rounded-md">
                    <option value="">Prefer not to say</option>
                    <option value="heterosexual">Heterosexual / Straight</option>
                    <option value="gay">Gay / Lesbian</option>
                    <option value="bisexual">Bisexual</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="age_range">Age Range</Label>
                  <select id="age_range" value={formData.diversity.age_range} onChange={(e) => handleInputChange('diversity', 'age_range', e.target.value)} className="w-full px-3 py-2 border rounded-md">
                    <option value="">Prefer not to say</option>
                    <option value="18-24">18-24</option>
                    <option value="25-34">25-34</option>
                    <option value="35-44">35-44</option>
                    <option value="45-54">45-54</option>
                    <option value="55-64">55-64</option>
                    <option value="65+">65+</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 7: GDPR Consent */}
        {visibleSections.gdpr_consent && (
          <Card className="mb-6 border-2 border-indigo-200">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Scale className="w-5 h-5 mr-2 text-indigo-600" />
                3. GDPR Consent (Required)
              </CardTitle>
              <CardDescription>Please read and accept the following to proceed</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <label className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <Checkbox checked={formData.gdpr_consent.consent_process_data} onCheckedChange={(checked) => handleInputChange('gdpr_consent', 'consent_process_data', checked)} />
                <div>
                  <p className="font-medium">Consent to Process Data *</p>
                  <p className="text-sm text-gray-600">I consent to RefrX processing my personal data for employment verification and recruitment purposes.</p>
                </div>
              </label>

              <label className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <Checkbox checked={formData.gdpr_consent.consent_contact} onCheckedChange={(checked) => handleInputChange('gdpr_consent', 'consent_contact', checked)} />
                <div>
                  <p className="font-medium">Consent to Contact *</p>
                  <p className="text-sm text-gray-600">I consent to being contacted by RefrX and potential employers regarding job opportunities.</p>
                </div>
              </label>

              <label className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <Checkbox checked={formData.gdpr_consent.consent_store_data} onCheckedChange={(checked) => handleInputChange('gdpr_consent', 'consent_store_data', checked)} />
                <div>
                  <p className="font-medium">Consent to Store Data *</p>
                  <p className="text-sm text-gray-600">I consent to RefrX storing my data until I request account deletion.</p>
                </div>
              </label>

              {(!formData.gdpr_consent.consent_process_data || !formData.gdpr_consent.consent_contact || !formData.gdpr_consent.consent_store_data) && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5" />
                  <p className="text-sm text-red-800">All consent checkboxes are required to submit this form.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => navigate('/dashboard')} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Personal Details
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
